import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';
import { useComisionLedger } from '../hooks/useComisionLedger';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { PencilSquareIcon, CheckIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

// Same money-input helpers the línea de afianzamiento editor uses
// (ClientAfianzadoras): live comma formatting as the user types.
function formatInputWithCommas(raw) {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
}

function parseFormattedNumber(formatted) {
  return parseFloat(String(formatted).replace(/[^0-9]/g, '')) || 0;
}

/**
 * MetasPrimaCard — per-afianzadora prima production targets for the year.
 *
 * The owner sets a meta per afianzadora (orgs/{org}/metas/{year}, a map of
 * afianzadora → target); progress is the prima booked in the comisión ledger
 * this year, so it needs no contracts and stays consistent with the Comisiones
 * page. Selling more prima with a carrier earns better treatment (convention
 * invites, terms) — this card is the scoreboard for that.
 */
export function MetasPrimaCard() {
  const navigate = useNavigate();
  const { userData, isOwner } = useUser();
  const { movements } = useComisionLedger();
  const year = new Date().getFullYear();

  const [metas, setMetas] = useState({});
  const [editing, setEditing] = useState(null); // afianzadora full name
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userData?.org) return undefined;
    const ref = doc(db, 'orgs', userData.org, 'metas', String(year));
    return onSnapshot(ref, (snap) => setMetas(snap.data()?.metas || {}), (e) => {
      console.error('metas listener error', e);
    });
  }, [userData?.org, year]);

  // Prima booked this year per afianzadora, straight from the ledger. Signed:
  // adicionales add, sustituye deltas subtract — net prima emitida.
  const primaPorAfianzadora = useMemo(() => {
    const map = new Map();
    movements.forEach((m) => {
      const ms = Number(m.fecha) || 0;
      if (!ms || new Date(ms).getFullYear() !== year) return;
      const prima = Number(m.prima) || 0;
      if (!prima) return;
      const key = m.afianzadora || 'Sin afianzadora';
      map.set(key, (map.get(key) || 0) + prima);
    });
    return map;
  }, [movements, year]);

  const rows = useMemo(() => {
    const keys = new Set([...primaPorAfianzadora.keys(), ...Object.keys(metas)]);
    return Array.from(keys)
      .map((key) => {
        const vendida = primaPorAfianzadora.get(key) || 0;
        const meta = Number(metas[key]) || 0;
        return {
          key,
          vendida,
          meta,
          pct: meta > 0 ? (vendida / meta) * 100 : null,
        };
      })
      .sort((a, b) => (b.meta || 0) - (a.meta || 0) || b.vendida - a.vendida);
  }, [primaPorAfianzadora, metas]);

  const saveMeta = async (afianzadora) => {
    const value = parseFormattedNumber(draft);
    if (!value && draft.trim() !== '' && draft.trim() !== '0') {
      window.alert('La meta debe ser un monto válido.');
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'orgs', userData.org, 'metas', String(year)),
        {
          metas: { ...metas, [afianzadora]: value },
          updatedAt: serverTimestamp(),
          updatedBy: userData?.uid || null,
        },
        { merge: true }
      );
      setEditing(null);
    } catch (e) {
      console.error('save meta failed', e);
      window.alert(e.message || 'No se pudo guardar la meta.');
    } finally {
      setSaving(false);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-1">
        <Heading level={3} className="text-lg text-azul-surelia">Metas de prima {year}</Heading>
        <button
          type="button"
          onClick={() => navigate('/app/reportes?reporte=metas-afianzadora')}
          className="inline-flex items-center gap-0.5 text-xs font-medium text-azul-surelia hover:underline shrink-0"
        >
          Ver reporte
          <ChevronRightIcon className="w-3.5 h-3.5" />
        </button>
      </div>
      <Text className="text-xs text-azul-surelia/60 mb-4">
        Prima emitida este año contra la meta de venta con cada afianzadora.
      </Text>

      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <span className="text-sm font-medium text-zinc-900" title={r.key}>
                {r.key}
              </span>
              <span className="text-sm tabular-nums text-zinc-700">
                <span className="font-semibold text-azul-surelia">{fmtMXN(r.vendida)}</span>
                <span className="text-gray-400 mx-1">de</span>
                {editing === r.key ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center rounded border border-azul-surelia/40 bg-white px-1.5 py-0.5 focus-within:ring-1 focus-within:ring-azul-surelia">
                      <span className="text-zinc-400 mr-1">$</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={draft}
                        onChange={(e) => setDraft(formatInputWithCommas(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveMeta(r.key);
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        disabled={saving}
                        autoFocus
                        placeholder="80,000,000"
                        className="w-28 text-right text-sm outline-none bg-transparent"
                      />
                    </span>
                    <button type="button" onClick={() => saveMeta(r.key)} disabled={saving}
                      className="text-emerald-600 hover:text-emerald-800">
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setEditing(null)} disabled={saving}
                      className="text-gray-400 hover:text-red-500">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <span className={r.meta > 0 ? 'font-semibold text-zinc-800' : 'text-gray-400'}>
                      {r.meta > 0 ? fmtMXN(r.meta) : 'sin meta'}
                    </span>
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => { setEditing(r.key); setDraft(r.meta ? formatInputWithCommas(String(r.meta)) : ''); }}
                        title="Definir meta de prima para esta afianzadora"
                        className="text-gray-300 hover:text-azul-surelia"
                      >
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                )}
                {r.pct !== null && (
                  <span className={`ml-2 text-xs font-semibold ${r.pct >= 100 ? 'text-emerald-700' : 'text-gray-500'}`}>
                    {Math.round(r.pct)}%
                  </span>
                )}
              </span>
            </div>
            {/* Meter: progress toward the meta — a real ratio against a limit,
                so the fill and the % always agree. No meta, no meter. */}
            {r.meta > 0 && (
              <div className="h-2 rounded-full bg-azul-surelia/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    r.pct >= 100 ? 'bg-emerald-500' : 'bg-azul-surelia'
                  }`}
                  style={{ width: `${Math.min(r.pct, 100)}%` }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default MetasPrimaCard;
